import { Box } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Text } from '../../../Components/Base';
import { LoLReportModal } from '../../../Containers/LoLResultModal';
import {
  LoLExaminationSection,
  LoLExaminationTag,
} from '../../../Contexts/LoLExaminationProvider/types';
import {
  Lol_Exam_Result_Type,
  QmslolExamResult,
  useGetPreferenceReportByExamPaperIdQuery as useGetPreferenceReportByExamPaperId,
} from '../../../generated/graphql';
import useDisclosure from '../../../Utils/useDisclosure';
import AcheivementCard, { AcheivementCardLoading } from '../Components/AcheivementCard';
import { coreTraitList } from '../constants';
import { MilestoneTags } from '../types';

type CoreTraitListProps = {
  examPaperId?: string;
  milestones?: MilestoneTags[];
  finishedSections?: string[];
};

const TRAIT_GROUPS = [
  { tag: LoLExaminationSection.VALUE, titleKey: 'VALUE_SECTION' },
  { tag: LoLExaminationSection.RIASEC, titleKey: 'RIASEC_SECTION' },
  { tag: LoLExaminationSection.STRENGTH, titleKey: 'STRENGTH_SECTION' },
] as const;

const ENALBED_AFTER = {
  [Lol_Exam_Result_Type.FirstRiasec]: LoLExaminationTag.RIASEC,
  [Lol_Exam_Result_Type.SecondRiasec]: LoLExaminationTag.ACTIVITY,
  [Lol_Exam_Result_Type.FirstValue]: LoLExaminationTag.VALUE,
  [Lol_Exam_Result_Type.SecondValue]: LoLExaminationTag.VALUE,
  [Lol_Exam_Result_Type.ThirdValue]: LoLExaminationTag.VALUE,
  [Lol_Exam_Result_Type.FirstStrength]: LoLExaminationTag.STRENGTH,
  [Lol_Exam_Result_Type.SecondStrength]: LoLExaminationTag.STRENGTH,
  [Lol_Exam_Result_Type.ThirdStrength]: LoLExaminationTag.STRENGTH,
};

const isFinishedSection = (check: keyof typeof ENALBED_AFTER, finishedSections: string[]) => {
  return finishedSections.some((section) => ENALBED_AFTER[check] === section);
};

const CoreTraitList = (props: CoreTraitListProps) => {
  const { examPaperId = '', milestones = [], finishedSections = [] } = props;
  const { isOpen, open, close } = useDisclosure();
  const { t } = useTranslation('lolResults');
  const [currentCoreTrait, setCurrentCoreTrait] = useState<QmslolExamResult>();
  const [currentResultGeneralData, setCurrentResultGeneralData] = useState<{
    title: string;
    subtitle: string;
  }>({
    title: '',
    subtitle: '',
  });

  const { data: preferenceData, loading } = useGetPreferenceReportByExamPaperId({
    variables: {
      examPaperId,
    },
    fetchPolicy: 'network-only',
  });

  const preferences = preferenceData?.qmsGetLOLExamReport.preferences ?? [];

  const handleCardClick = (title: string, subtitle: string, coretrait?: QmslolExamResult) => {
    setCurrentCoreTrait(coretrait ?? undefined);
    setCurrentResultGeneralData({ title, subtitle });
    open();
  };

  const handleCloseModal = () => {
    close();
  };

  const milestoneTags = milestones.flatMap((milestone) => {
    return milestone.tags;
  });

  const filterCoreTraitList = coreTraitList
    .filter((coreTrait) => {
      const tags = coreTrait?.tags ?? [];
      const hasTagsInMilestoneTags = tags.every((tag) => milestoneTags.includes(tag));

      return hasTagsInMilestoneTags;
    })
    .map((coreTrait) => {
      const isLocked = !isFinishedSection(
        coreTrait.type as keyof typeof ENALBED_AFTER,
        finishedSections
      );
      return {
        ...coreTrait,
        locked: isLocked,
      };
    });

  const filterPreferences = preferences.filter((preference) => {
    return filterCoreTraitList.some(
      (coreTrait) => coreTrait.type === preference.type && !coreTrait.locked
    );
  });

  return (
    <>
      <Flex flexDirection={'column'} gap={4}>
        {TRAIT_GROUPS.map(({ tag, titleKey }) => {
          const groupTraits = filterCoreTraitList.filter((ct) => ct.tags?.includes(tag));
          const groupPreferences = filterPreferences.filter((p) =>
            groupTraits.some((ct) => ct.type === p.type)
          );

          if (groupTraits.length === 0) return null;

          return (
            <Flex key={tag} flexDirection={'column'} gap={2}>
              <Box>
                <Text
                  variant="h2"
                  fontWeight={500}
                  mr={1}
                  display={'inline-block'}
                  color={'lolText.primary'}
                >
                  {t(titleKey)}
                </Text>
                <Text
                  variant="h3"
                  fontWeight={400}
                  lineHeight={1.4}
                  color={'lolText.secondary'}
                  display={'inline-block'}
                >
                  {` (${groupPreferences.length}/${groupTraits.length})`}
                </Text>
              </Box>
              <Flex flexDirection={'column'} gap={2} mb={3}>
                {groupTraits.map((coreTrait, index) => {
                  const coreTraitResult = filterPreferences?.find(
                    (preferenceResult) => preferenceResult.type === coreTrait.type
                  );

                  const isLocked = coreTrait.locked;
                  const conditionNumber = coreTrait?.conditionNumber;
                  const refCode = coreTraitResult?.refCode ?? '';
                  const defaultTitle = t(`CORE_TRAIT.${coreTrait.title}`, { ns: 'lolCommon' });
                  const title = coreTraitResult?.title ?? defaultTitle;
                  const subtitle = t(`CORE_TRAIT.${coreTrait.subtitle}`, { ns: 'lolCommon' });

                  if (loading) {
                    return <AcheivementCardLoading key={`loading-${coreTrait.type}-${index}`} />;
                  }

                  return (
                    <AcheivementCard
                      key={`${coreTrait.type}-${index}`}
                      title={title}
                      subtitle={subtitle}
                      conditionNumber={conditionNumber}
                      resultType={coreTrait.type}
                      locked={isLocked}
                      refCode={refCode}
                      onClick={() => handleCardClick(title, subtitle, coreTraitResult)}
                    />
                  );
                })}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      <LoLReportModal
        isOpen={isOpen}
        handleClose={handleCloseModal}
        displayData={currentCoreTrait}
        title={currentResultGeneralData.title}
        subtitle={currentResultGeneralData.subtitle}
      />
    </>
  );
};

export default CoreTraitList;
